import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../../shared/_TBComponents/DatePicker';
import Input from '../../../../../shared/_TBComponents/Input';
import Modal from '../../../../../shared/_TBComponents/Modal';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../../shared/_TBComponents/Text';
import { showSuccessToast, showErrorToast } from '../../../../../shared/utils/toastManager';
import { createBlackoutDate } from '../../../ducks/navigatorActions';

const SELECTBOX_STYLES = {
  control: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0',
    boxShadow: 'none',
    '&:hover': { borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0' },
  }),
  option: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: state.isFocused || state.isSelected ? 'rgba(119, 167, 255, 0.15)' : 'transparent',
    color: '#333333',
    '&:active': { backgroundColor: 'rgba(119, 167, 255, 0.25)' },
  }),
};

const FormLayout = styled(Form)`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderDivider = styled.div`
  border-top: 1px solid #D6D6D6;
  margin: 16px -24px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.div`
  margin-bottom: 8px;
`;

const DateRangeRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const DatePickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 24px -24px -24px;
  padding: 16px 24px 24px;
  border-top: 1px solid #EEEEEE;
`;

const AddBlackoutDateModal = ({ isOpen, onClose, onCreated }) => {
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const coaches = useSelector((state) => state.navigator.data.coaches);
  const groups = useSelector((state) => state.navigator.data.groups);
  const isCreatingBlackoutDate = useSelector((state) => state.navigator.ui.isCreatingBlackoutDate);

  const coachOptions = useMemo(
    () => coaches.map((c) => ({ id: c.id, name: `${c.first || ''} ${c.last || ''}`.trim() })),
    [coaches],
  );
  const groupOptions = useMemo(
    () => groups.map((g) => ({ id: g.id, name: g.name })),
    [groups],
  );

  const initialValues = {
    reason: '',
    coach: null,
    group: null,
    startDate: new Date(),
    endDate: null,
  };

  const validationSchema = Yup.object().shape({
    reason: Yup.string().required('A reason is required'),
    startDate: Yup.date().nullable().required('Start date is required'),
    endDate: Yup.date().nullable().required('End date is required')
      .min(Yup.ref('startDate'), 'End date cannot be before start date'),
  });

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    dispatch(createBlackoutDate(
      accountCode,
      {
        reason: values.reason,
        userId: values.coach?.id || 0,
        groupId: values.group?.id || 0,
        dateStart: format(values.startDate, 'yyyy-MM-dd'),
        dateEnd: format(values.endDate, 'yyyy-MM-dd'),
      },
      {
        onSuccess: (newBlackoutDate) => {
          showSuccessToast('Blackout date has been added.');
          setSubmitting(false);
          resetForm();
          if (onCreated) onCreated(newBlackoutDate);
          onClose();
        },
        onError: (err) => {
          showErrorToast(err?.response?.headers?.message || 'Unable to add blackout date. Please try again.');
          setSubmitting(false);
        },
      },
    ));
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      customBorderRadius='8px'
      customPadding='24px'
      customMaxWidth='500px'
      customMinWidth='min(500px, 100vw)'
      widthFitContent
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue,
          isSubmitting,
        }) => (
          <FormLayout autoComplete='off'>
            <ModalHeader>
              <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A' }}>
                Add Blackout Dates
              </Text>
              <Button
                icon='remove'
                iconSize={15}
                onClick={onClose}
                fontSize='13px'
                txtColor='#9E9E9E'
                noBorder
                iconOnly
                styledWrapperProps={{ padding: '0' }}
              />
            </ModalHeader>
            <HeaderDivider />

            <Section>
              <Input
                label='Reason for Blackout'
                name='reason'
                type='text'
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Reason for Blackout'
                fontSize='14px'
                height='38px'
                width='100%'
                styledPropsLabel={{ fontWeight: 700 }}
                error={!!(touched.reason && errors.reason)}
                errorMsg={errors.reason || ''}
                right='5px'
                top='38px'
              />
            </Section>

            <Section>
              <FieldLabel>
                <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
                  Apply to Specific Coach
                </Text>
                <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
                  Leave empty for all coaches
                </Text>
              </FieldLabel>
              <SelectBox
                options={coachOptions}
                value={values.coach}
                onChangeFunction={(opt) => setFieldValue('coach', opt)}
                placeholder='Select a coach (leave empty for all)'
                styledProps={{ border: '1px solid #EEEEEE' }}
                customStyles={SELECTBOX_STYLES}
                menuPortalTarget={document.body}
                height='auto'
              />
            </Section>

            <Section>
              <FieldLabel>
                <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
                  Apply to Specific Athlete Group
                </Text>
                <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
                  Leave empty for all groups
                </Text>
              </FieldLabel>
              <SelectBox
                options={groupOptions}
                value={values.group}
                onChangeFunction={(opt) => setFieldValue('group', opt)}
                placeholder='Select group (leave empty for all)'
                styledProps={{ border: '1px solid #EEEEEE' }}
                customStyles={SELECTBOX_STYLES}
                menuPortalTarget={document.body}
                height='auto'
              />
            </Section>

            <Section>
              <FieldLabel>
                <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
                  Select Date Range to Hide Workout Results
                </Text>
              </FieldLabel>
              <DateRangeRow>
                <DatePickerField>
                  <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Start Date</Text>
                  <DatePicker
                    singleDate
                    startDate={values.startDate}
                    setStartDate={(date) => {
                      setFieldValue('startDate', date);
                      if (date && values.endDate && values.endDate < date) {
                        setFieldValue('endDate', null);
                      }
                    }}
                    maxDate={null}
                    placeholderText='Select Start Date'
                    textColor='#333333'
                  />
                  {submitCount > 0 && errors.startDate && (
                    <Text styledTextProps={{ fontSize: '13px', fontWeight: 700, color: '#CC0404' }}>
                      {errors.startDate}
                    </Text>
                  )}
                </DatePickerField>
                <DatePickerField>
                  <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>End Date</Text>
                  <DatePicker
                    singleDate
                    startDate={values.endDate}
                    setStartDate={(date) => setFieldValue('endDate', date)}
                    minDate={values.startDate || undefined}
                    maxDate={null}
                    placeholderText='Select End Date'
                    textColor='#333333'
                  />
                  {submitCount > 0 && errors.endDate && (
                    <Text styledTextProps={{ fontSize: '13px', fontWeight: 700, color: '#CC0404' }}>
                      {errors.endDate}
                    </Text>
                  )}
                </DatePickerField>
              </DateRangeRow>
            </Section>

            <Footer>
              <Button
                type='button'
                name='Close'
                fontSize='14px'
                bgColor='#FFF'
                borderColor='#EEEEEE'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={onClose}
              />
              <Button
                type='submit'
                name={isSubmitting || isCreatingBlackoutDate ? 'Saving...' : 'Submit'}
                fontSize='14px'
                txtColor='#FFF'
                bgColor='#1A1A1A'
                borderColor='#1A1A1A'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                disabled={isSubmitting || isCreatingBlackoutDate}
              />
            </Footer>
          </FormLayout>
        )}
      </Formik>
    </Modal>
  );
};

AddBlackoutDateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};

AddBlackoutDateModal.defaultProps = {
  onCreated: null,
};

export default AddBlackoutDateModal;
